/**
 * The site audit's seven scored dimensions — the single vocabulary shared by
 * the audit pipeline, the dashboard module card, and the L2 drill-down.
 *
 * Findings carry NO dimension field (the schema comment claiming otherwise is
 * stale). The only durable signal is the `issueKey` namespace, so the mapping
 * from key prefix to dimension lives here rather than being re-derived by each
 * consumer. Anything outside the seven scored dimensions — freshness, chunk
 * quality, grounding, browser-pipeline diagnostics — collapses to "other"
 * instead of being silently dropped: those findings are still real work items,
 * they just don't move a dimension score.
 *
 * Pure constants and pure functions: safe to export from `@repo/core/client`.
 */

/** dimensionScores keys, in the audit's canonical order. */
export const AUDIT_DIMENSION_KEYS = [
	"staticContent",
	"performance",
	"structuredData",
	"eeat",
	"readability",
	"citationReadiness",
	"accessibility",
] as const;

export type AuditDimensionKey = (typeof AUDIT_DIMENSION_KEYS)[number];

/** A finding's dimension: one of the seven, or the catch-all bucket. */
export type AuditFindingDimension = AuditDimensionKey | "other";

/**
 * issueKey prefix → dimension. Ordered longest-prefix-first so the two-segment
 * E-E-A-T namespaces win before any single-segment prefix could shadow them.
 */
const ISSUE_KEY_PREFIXES: ReadonlyArray<readonly [string, AuditDimensionKey]> =
	[
		["eeat.brand.", "eeat"],
		["eeat.page.", "eeat"],
		["static_content.", "staticContent"],
		["performance.", "performance"],
		["structuredData.", "structuredData"],
		["readability.", "readability"],
		["citation.", "citationReadiness"],
		["accessibility.", "accessibility"],
	];

/**
 * Map a finding's `issueKey` to the dimension whose score it contributes to.
 * Unknown, empty, and null keys fall through to "other".
 */
export function dimensionForIssueKey(
	issueKey: string | null | undefined,
): AuditFindingDimension {
	if (!issueKey) {
		return "other";
	}
	for (const [prefix, dimension] of ISSUE_KEY_PREFIXES) {
		if (issueKey.startsWith(prefix)) {
			return dimension;
		}
	}
	return "other";
}
